// Investor surface data fetchers.
//
// Phase 5 §5.6.1 — the investor dashboard reads several dossier paths
// + one new admin endpoint. The functions here are thin typed wrappers
// around `api.get(...)`; component code stays decoupled from the
// projector URL scheme. Phase 6 swaps these for SSE-driven live
// versions.
//
// Identity in dev-bypass mode: hardcoded to the demo person/investor
// per INVESTOR_UX_2026-04-28 §9 Q1. Real Privy → personId → investorId
// resolution lands in Phase 6 alongside the agent runtime.

import { parse as yamlParse } from 'yaml';
import { api } from '@rmint/shell';
import { env } from '@rmint/shell';

export type InvestorIdentity = {
  personId: string;
  investorId: string;
  walletAddress: string | undefined;
};

const DEV_BYPASS_IDENTITY: InvestorIdentity = {
  // Must match the dev-bypass Privy DID — the seed (setup-local-investor)
  // plants persons/{privyDid}/profile.yaml and every event subject uses
  // personId: <privyDid>. A divergent slug here 404s the profile read and
  // the dashboard silently falls back to a generic name. See AppLayout's
  // `did:privy:dev-bypass` banner + safe.ts (which keys persons/ on privyDid).
  personId: 'did:privy:dev-bypass',
  investorId: 'inv_demo_alice',
  walletAddress: undefined,
};

// Map the current auth context to a dossier identity. Falls back to
// the dev-bypass synthetic identity when DEV_AUTH_BYPASS is set; otherwise
// derives from the Privy DID via a Phase 6 endpoint (not yet wired).
export function resolveIdentity(privyDid: string | undefined): InvestorIdentity {
  if (env.devAuthBypass) return DEV_BYPASS_IDENTITY;
  if (!privyDid) return DEV_BYPASS_IDENTITY; // pre-login fallback; routes guard with `authenticated`
  // Phase 6 wires the real resolver. Until then a Privy DID falls
  // through to the dev identity so the dashboard at least renders.
  return DEV_BYPASS_IDENTITY;
}

// ─── YAML reader ─────────────────────────────────────────────────

async function readYaml<T = unknown>(path: string): Promise<T | null> {
  const res = await api.get(path, { responseType: 'text' });
  if (res.status === 404) return null;
  if (res.status >= 400) {
    throw new Error(`dossier read failed: ${res.status} ${path}`);
  }
  const text = typeof res.data === 'string' ? res.data : '';
  return yamlParse(text) as T;
}

// ─── Person profile (name, email) ────────────────────────────────

export type PersonProfile = {
  email?: string;
  name?: string;
  privyDid?: string;
  // The metagraph allows additional fields; cell renderers pluck what
  // they need. Keeping the type loose lets the dashboard tolerate
  // older / newer profile schemas without crashing.
  [key: string]: unknown;
};

export async function fetchPersonProfile(personId: string): Promise<PersonProfile | null> {
  return readYaml<PersonProfile>(`/dossier/persons/${encodeURIComponent(personId)}/profile.yaml`);
}

// ─── Investor lifecycle reads ────────────────────────────────────

// The "investors/" tree is a projection target, not an entity
// lifecycle. Phase 5 reads:
//   investors/{id}/lifecycle/02-kyc-submitted/{eventId}.yaml  (zero or more)
//   investors/{id}/lifecycle/03-kyc-verified/attestation.yaml (when verified)
//   investors/{id}/identity/kyc/decisions/{eventId}.yaml      (per decision)
//
// For the dashboard we only need the verification-status indicator,
// derived from whether 03-kyc-verified/attestation.yaml exists.

export type KycStatus = 'verified' | 'submitted' | 'rejected' | 'unknown';

export async function fetchKycStatus(investorId: string): Promise<KycStatus> {
  // Try the verified attestation first (most specific signal).
  const verified = await readYaml(
    `/dossier/investors/${encodeURIComponent(investorId)}/lifecycle/03-kyc-verified/attestation.yaml`,
  );
  if (verified) return 'verified';
  // Fall back to checking for any kyc.submitted marker.
  // We can't list directories from /dossier/*, so we pivot to the
  // /admin/investor/{id}/subscriptions endpoint — its presence
  // indicates the investor has at least one kyc.submitted on record.
  const subs = await fetchSubscriptionIds(investorId);
  if (subs.length > 0) return 'submitted';
  return 'unknown';
}

// ─── Subscription discovery + state ──────────────────────────────

export async function fetchSubscriptionIds(investorId: string): Promise<string[]> {
  const res = await api.get(
    `/admin/investor/${encodeURIComponent(investorId)}/subscriptions`,
    { responseType: 'json' },
  );
  if (res.status >= 400) {
    throw new Error(`subscriptions list failed: ${res.status}`);
  }
  const body = res.data as { subscriptionIds?: string[] };
  return body.subscriptionIds ?? [];
}

// state.current.yaml is regenerated by the projector after each
// event. Actual on-disk shape (verified against projector output):
//   type: subscription
//   id: sub_xxx
//   state: 03-kyc-verified
//   version: 3
//   last_event_id: evt_ver_xxx
//   last_event_path: subscriptions/.../timeline/.../yyy.yaml
//   updated_at: 2026-04-28T13:45:51.515Z
//   data: { investorId: ..., ... }
//
// Field names use snake_case in the YAML; we expose them as camelCase
// helpers on the wrapper below so the rest of the frontend stays in
// the JS convention.
export type SubscriptionStateRaw = {
  type?: string;
  id?: string;
  state?: string;
  version?: number;
  last_event_id?: string;
  last_event_path?: string;
  updated_at?: string;
  data?: Record<string, unknown>;
};

export type SubscriptionState = {
  entityId: string;
  currentPhase: string;
  lastEvent?: {
    eventId?: string;
    eventType?: string;
    occurredAt?: string;
  };
  raw: SubscriptionStateRaw;
};

function deriveLastEventType(path?: string): string | undefined {
  if (!path) return undefined;
  // last_event_path format: subscriptions/X/timeline/YYYY-MM-DD/HHMMSS.SSSZ-{eventId}-{eventType}.yaml
  const filename = path.split('/').pop() ?? '';
  const stem = filename.replace(/\.yaml$/, '');
  // Match: HHMMSS.SSSZ-EVENTID-EVENTTYPE
  const match = stem.match(/^[^-]+-([^-]+(?:_[^-]+)*)-(.+)$/);
  return match?.[2];
}

function deriveOccurredAt(updatedAt?: string): string | undefined {
  return updatedAt;
}

// Returns `null` when the subscription's state file is missing (404) — used
// by the chat-canvas to disambiguate "no such subscription" from a real
// fetch error. Throws on any other 4xx/5xx (including ACL 403). The
// `useSubscriptionState` hook preserves the distinction: a 404 surfaces
// as `{ state: null, error: null }`; transport / authorization failures
// surface as `{ state: null, error: Error }`.
export async function fetchSubscriptionState(
  subscriptionId: string,
): Promise<SubscriptionState | null> {
  const raw = await readYaml<SubscriptionStateRaw>(
    `/dossier/subscriptions/${encodeURIComponent(subscriptionId)}/state.current.yaml`,
  );
  if (!raw) return null;
  return {
    entityId: raw.id ?? subscriptionId,
    currentPhase: raw.state ?? 'unknown',
    lastEvent: raw.last_event_id
      ? {
          eventId: raw.last_event_id,
          eventType: deriveLastEventType(raw.last_event_path),
          occurredAt: deriveOccurredAt(raw.updated_at),
        }
      : undefined,
    raw,
  };
}

// Reads `lifecycle/04-allocated/allocation-issued.yaml` for the original
// allocation amount (capUsd). The Subscribe Fund widget needs this because
// the projector's state.current materializer REPLACES `data` per event,
// so post-sign the allocation amount is no longer in state.current.data.
// (Tracked separately as an architectural concern — see CLEANUP plan
// for the materializer accumulation refactor.)
//
// Returns null if the file doesn't exist (e.g. subscription that didn't
// go through the proper allocate step).
export type AllocationIssued = {
  data?: { capUsd?: number; offeringId?: string; officerId?: string; expiresAt?: string };
};

export async function fetchAllocationCapUsd(subscriptionId: string): Promise<number | null> {
  const raw = await readYaml<AllocationIssued>(
    `/dossier/subscriptions/${encodeURIComponent(subscriptionId)}/lifecycle/04-allocated/allocation-issued.yaml`,
  );
  return raw?.data?.capUsd ?? null;
}

// kyc.submitted marker body carries the subject (investorId, rmintId)
// — the dashboard uses this to show restaurant context per
// subscription.
export async function fetchSubscriptionSubject(
  subscriptionId: string,
): Promise<{ investorId?: string; rmintId?: string } | null> {
  const marker = await readYaml<{ subject?: { investorId?: string; rmintId?: string } }>(
    `/dossier/subscriptions/${encodeURIComponent(subscriptionId)}/lifecycle/02-kyc-submitted/kyc-submitted.yaml`,
  );
  return marker?.subject ?? null;
}

// ─── Composite: enriched subscription card data ──────────────────

export type EnrichedSubscription = {
  subscriptionId: string;
  state: SubscriptionState | null;
  subject: { investorId?: string; rmintId?: string } | null;
};

export async function fetchEnrichedSubscriptions(
  investorId: string,
): Promise<EnrichedSubscription[]> {
  const ids = await fetchSubscriptionIds(investorId);
  return Promise.all(
    ids.map(async (subscriptionId) => {
      const [state, subject] = await Promise.all([
        fetchSubscriptionState(subscriptionId),
        fetchSubscriptionSubject(subscriptionId),
      ]);
      return { subscriptionId, state, subject };
    }),
  );
}
